import { useState, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Phone,
  CreditCard,
  Store,
  Package,
  Shield,
  Image as ImageIcon,
  SquareUser,
  ArrowLeft,
} from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import Swal from "sweetalert2";

const SellRequestForm = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({
    store_name: "",
    seller_name: "",
    seller_phone: "",
    seller_divisi: "",
    seller_no_pegawai: "",
    product_name: "",
    product_category: "",
    product_system: "",
    product_price: "",
    product_stock: "",
    product_description: "",
    product_photo: null as File | null,
    bank_account: "",
    provider_bank: "",
    owner_bank_account: "",
  });
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const productCategories = [
    "Fashion & Pakaian",
    "Elektronik & Gadget",
    "Makanan & Minuman",
    "Kesehatan & Kecantikan",
    "Rumah Tangga & Dekorasi",
    "Hobi & Koleksi",
    "Otomotif & Transport",
    "Buku & Edukasi",
    "Olahraga & Outdoor",
    "Lainnya",
  ];

  const listProviderBank = [
    "BCA",
    "Mandiri",
    "BRI",
    "BNI",
    "CIMB Niaga",
    "Danamon",
    "Permata Bank",
    "Bank Syariah Indonesia (BSI)",
    "Bank Jago",
    "Bank Neo Commerce",
    "Bank Sinarmas",
    "Bank Mega",
    "Bank Bukopin",
    "Bank BTPN",
    "Bank Panin",
    "Bank OCBC NISP",
    "Bank UOB",
    "Bank DBS",
    "Bank Maybank",
    "Lainnya",
  ];

  const listDivisiSeller = [
    "IT",
    "Keuangan",
    "SDM/PU",
    "Keamanan",
    "Driver",
    "MK (Marketing Kredit)",
    "BIL/BIAS",
    "Rooted",
    "Marketing",
    "OB/Satpam TC",
  ];

  //handleSystemChange
  const handleSystemChange = (value) => {
    setForm({ ...form, product_system: value });
  };

  // Secure validation functions
  const validateForm = () => {
    const errors: string[] = [];

    if (!form.store_name.trim() || form.store_name.length < 3) {
      errors.push("Nama toko minimal 3 karakter");
    }

    if (!form.seller_name.trim() || form.seller_name.length < 3) {
      errors.push("Nama penjual minimal 3 karakter");
    }

    const phoneRegex = /^(\+62|62|0)[0-9]{9,12}$/;
    if (!phoneRegex.test(form.seller_phone.replace(/\s/g, ""))) {
      errors.push("Format nomor HP tidak valid");
    }

    if (!form.product_name.trim() || form.product_name.length < 3) {
      errors.push("Nama produk minimal 3 karakter");
    }

    if (!form.product_category) {
      errors.push("Pilih kategori produk");
    }

    const price = Number(form.product_price);
    if (isNaN(price) || price < 1000) {
      errors.push("Harga produk harus lebih dari Rp 1.000");
    }

    const bankAccount = form.bank_account.trim();
    if (!/^[0-9]{8,20}$/.test(bankAccount)) {
      errors.push("Nomor rekening harus 8-20 digit angka");
    }

    const noPegawai = form.seller_no_pegawai.trim();
    if (!/^[0-9]{8}$/.test(noPegawai)) {
      errors.push("Nomor pegawai harus berupa angka 8 digit");
    }

    if (!form.provider_bank) {
      errors.push("Pilih bank penerima");
    }

    if (form.product_photo && form.product_photo.size > 5 * 1024 * 1024) {
      errors.push("Ukuran foto maksimal 5MB");
    }

    setValidationErrors(errors);
    return errors.length === 0;
  };

  // Handler untuk input biasa (nama toko, dll)
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    const sanitizedValue = value.replace(
      /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
      ""
    );
    setForm({ ...form, [name]: sanitizedValue });

    if (validationErrors.length > 0) {
      setValidationErrors([]);
    }
  };

  // Handler khusus untuk price
  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Hanya angka, tanpa karakter lain
    const rawValue = e.target.value.replace(/\D/g, "");
    setForm({ ...form, product_price: rawValue });
    if (validationErrors.length > 0) setValidationErrors([]);
  };

  console.log("Form State:", form, "Iki lo perubahan form");

  const handleCategoryChange = (value: string) => {
    setForm({ ...form, product_category: value });
    if (validationErrors.length > 0) {
      setValidationErrors([]);
    }
  };

  const handleBankChange = (value: string) => {
    setForm({ ...form, provider_bank: value });
    if (validationErrors.length > 0) {
      setValidationErrors([]);
    }
  };
  console.log("Bank:", form.provider_bank);

  const handleDivisiChange = (value: string) => {
    setForm({ ...form, seller_divisi: value });
    if (validationErrors.length > 0) {
      setValidationErrors([]);
    }
  };
  console.log("Divisi:", form.seller_divisi);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;

    if (file) {
      const allowedTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/webp",
      ];
      if (!allowedTypes.includes(file.type)) {
        toast({
          title: "Format File Tidak Valid",
          description: "Hanya file JPEG, PNG, dan WEBP yang diperbolehkan",
          variant: "destructive",
        });
        return;
      }
    }

    setForm({ ...form, product_photo: file });
    if (validationErrors.length > 0) {
      setValidationErrors([]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;

    if (!validateForm()) {
      Swal.fire({
        icon: "error",
        title: "Validasi Gagal",
        html: `
      <ul style="text-align: left;">
        ${validationErrors.map((err) => `<li>${err}</li>`).join("")}
      </ul>
    `,
        confirmButtonText: "Oke",
        customClass: {
          popup: "text-left",
        },
      });
      return;
    }

    setLoading(true);

    try {
      let photoUrl = "";
      if (form.product_photo) {
        const fileExt = form.product_photo.name.split(".").pop();
        const fileName = `${Date.now()}_${Math.random()
          .toString(36)
          .substring(7)}.${fileExt}`;

        const { data, error: uploadError } = await supabase.storage
          .from("sell-products")
          .upload(`public/${fileName}`, form.product_photo, {
            cacheControl: "3600",
            upsert: false,
          });

        if (uploadError) {
          throw new Error(`Gagal upload foto: ${uploadError.message}`);
        }
        photoUrl = data?.path || "";
      }

      const { error: insertError } = await supabase
        .from("sell_requests")
        .insert({
          name: form.store_name.trim(),
          seller_name: form.seller_name.trim(),
          seller_phone: form.seller_phone.trim(),
          product_name: form.product_name.trim(),
          product_category: form.product_category,
          product_price: Number(form.product_price),
          product_stock: form.product_stock.trim()
            ? Number(form.product_stock)
            : null,
          seller_no_pegawai: form.seller_no_pegawai.trim()
            ? Number(form.seller_no_pegawai.trim())
            : null,
          product_system: form.product_system,
          seller_divisi: form.seller_divisi,
          product_description: form.product_description.trim(),
          product_photo: photoUrl,
          bank_account: form.bank_account.trim(),
          owner_bank_account: form.owner_bank_account.trim(),
          provider_bank: form.provider_bank,
          status: "pending",
        });

      if (insertError) {
        throw new Error(`Gagal mengirim permintaan: ${insertError.message}`);
      }

      toast({
        title: "Berhasil!",
        description:
          "Permintaan titip jual berhasil dikirim. Tim kami akan menghubungi Anda dalam 1-2 hari kerja.",
        variant: "default",
      });

      // Reset form
      setForm({
        store_name: "",
        seller_name: "",
        seller_phone: "",
        seller_no_pegawai: "",
        product_name: "",
        seller_divisi: "",
        product_category: "",
        product_system: "",
        product_price: "",
        product_stock: "",
        product_description: "",
        product_photo: null,
        bank_account: "",
        provider_bank: "",
        owner_bank_account: "",
      });
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error: any) {
      console.error("[DEBUG] Submit error:", error);
      toast({
        title: "Gagal Mengirim",
        description:
          error.message ||
          "Terjadi kesalahan saat mengirim permintaan. Silakan coba lagi.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  function formatRupiah(product_price: string): string {
    // const number = value.replace(/\D/g, "");
    //   return number ? Number(number).toLocaleString("id-ID") : "";

    if (!product_price) return "";
    // Remove non-digit characters
    const number = product_price.replace(/\D/g, "");
    if (!number) return "";
    // Format as Indonesian Rupiah
    return Number(number).toLocaleString("id-ID");
  }

  return (
    <div className="min-h-screen bg-gradient-hero py-8">
      <div className="ml-4">
        <Button
          variant="independence"
          size="sm"
          onClick={() => (window.location.href = "/")}
        >
          <ArrowLeft className="w-4 h-4" />
          Kembali
        </Button>
      </div>
      <div className="container mx-auto px-4">
        <Card className="max-w-4xl mx-auto shadow-independence border-0">
          <CardHeader className="bg-gradient-independence text-primary-foreground rounded-t-lg">
            <div className="flex items-center gap-3">
              <Package className="h-8 w-8" />
              <div>
                <CardTitle className="text-2xl font-display font-bold">
                  Formulir Titip Jual Produk
                </CardTitle>
                <p className="text-primary-foreground/80 mt-1 font-medium">
                  Bergabunglah dengan marketplace Bazaria dan mulai berjualan
                </p>
              </div>
            </div>
          </CardHeader>

          <CardContent className="p-8 bg-gradient-card">
            {/* Security Notice */}
            <Alert className="mb-6 border-accent/20 bg-accent/5">
              <Shield className="h-4 w-4 text-accent" />
              <AlertDescription className="text-foreground">
                <strong className="text-accent">Keamanan Terjamin:</strong> Data
                Anda dienkripsi dan dilindungi sistem keamanan berlapis
              </AlertDescription>
            </Alert>

            {/* Validation Errors */}
            {/* {validationErrors.length > 0 && (
              <Alert className="mb-6 border-destructive/20 bg-destructive/5">
                <AlertCircle className="h-4 w-4 text-destructive" />
                <AlertDescription className="text-destructive">
                  <strong>Harap perbaiki kesalahan berikut:</strong>
                  <ul className="list-disc list-inside mt-2">
                    {validationErrors.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            )} */}

            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Store Information Section */}
              <div className="space-y-6">
                <div className="flex items-center gap-2">
                  <Store className="h-5 w-5 text-primary" />
                  <h3 className="text-lg font-display font-semibold text-foreground">
                    Informasi Toko/Penjual
                  </h3>
                  <Badge
                    variant="outline"
                    className="text-primary border-primary/20 bg-primary/5"
                  >
                    Required
                  </Badge>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="space-y-2">
                    <Label
                      htmlFor="store_name"
                      className="text-sm font-medium text-foreground"
                    >
                      Nama Toko *
                    </Label>
                    <Input
                      id="store_name"
                      name="store_name"
                      value={form.store_name}
                      onChange={handleChange}
                      placeholder="Contoh: Toko Elektronik Sejahtera"
                      className="h-11 transition-smooth focus:ring-2 focus:ring-primary/20"
                      required
                      maxLength={100}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label
                      htmlFor="seller_name"
                      className="text-sm font-medium text-foreground"
                    >
                      Nama Lengkap Penjual *
                    </Label>
                    <Input
                      id="seller_name"
                      name="seller_name"
                      value={form.seller_name}
                      onChange={handleChange}
                      placeholder="Nama sesuai KTP"
                      className="h-11 transition-smooth focus:ring-2 focus:ring-primary/20"
                      required
                      maxLength={100}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label
                      htmlFor="seller_divisi"
                      className="text-sm font-medium text-foreground"
                    >
                      Divisi *
                    </Label>
                    <Select
                      value={form.seller_divisi}
                      onValueChange={handleDivisiChange}
                    >
                      <SelectTrigger className="h-11 transition-smooth focus:ring-2 focus:ring-primary/20">
                        <SelectValue placeholder="Pilih Divisi" />
                      </SelectTrigger>
                      <SelectContent>
                        {listDivisiSeller.map((divisi) => (
                          <SelectItem key={divisi} value={divisi}>
                            {divisi}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label
                      htmlFor="seller_no_pegawai"
                      className="text-sm font-medium flex items-center gap-2 text-foreground"
                    >
                      <SquareUser className="h-4 w-4" />
                      Nomor Pegawai *
                    </Label>
                    <Input
                      id="seller_no_pegawai"
                      name="seller_no_pegawai"
                      value={form.seller_no_pegawai}
                      onChange={handleChange}
                      placeholder="89387838"
                      type="number"
                      className="h-11 transition-smooth focus:ring-2 focus:ring-primary/20"
                      required
                      maxLength={8}
                    />
                    <p className="text-xs text-muted-foreground">
                      Nomor yang bisa dihubungi untuk koordinasi penjualan
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label
                      htmlFor="seller_phone"
                      className="text-sm font-medium flex items-center gap-2 text-foreground"
                    >
                      <Phone className="h-4 w-4" />
                      Nomor WhatsApp *
                    </Label>
                    <Input
                      id="seller_phone"
                      name="seller_phone"
                      value={form.seller_phone}
                      onChange={handleChange}
                      placeholder="081234567890"
                      type="number"
                      className="h-11 transition-smooth focus:ring-2 focus:ring-primary/20"
                      required
                      maxLength={15}
                    />
                    <p className="text-xs text-muted-foreground">
                      Nomor yang bisa dihubungi untuk koordinasi penjualan
                    </p>
                  </div>
                </div>
              </div>

              <Separator className="bg-border" />

              {/* Product Information Section */}
              <div className="space-y-6">
                <div className="flex items-center gap-2">
                  <Package className="h-5 w-5 text-accent" />
                  <h3 className="text-lg font-display font-semibold text-foreground">
                    Informasi Produk
                  </h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Nama Produk */}
                  <div className="space-y-2">
                    <Label
                      htmlFor="product_name"
                      className="text-sm font-medium text-foreground"
                    >
                      Nama Produk *
                    </Label>
                    <Input
                      id="product_name"
                      name="product_name"
                      value={form.product_name}
                      onChange={handleChange}
                      placeholder="Nama produk yang akan dijual"
                      className="h-11 transition-smooth focus:ring-2 focus:ring-primary/20"
                      required
                      maxLength={100}
                    />
                  </div>

                  {/* Kategori Produk */}
                  <div className="space-y-2">
                    <Label
                      htmlFor="product_category"
                      className="text-sm font-medium text-foreground"
                    >
                      Kategori Produk *
                    </Label>
                    <Select
                      value={form.product_category}
                      onValueChange={handleCategoryChange}
                    >
                      <SelectTrigger className="h-11 transition-smooth focus:ring-2 focus:ring-primary/20">
                        <SelectValue placeholder="Pilih kategori produk" />
                      </SelectTrigger>
                      <SelectContent>
                        {productCategories.map((category) => (
                          <SelectItem key={category} value={category}>
                            {category}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Harga Produk */}
                  <div className="space-y-2">
                    <Label
                      htmlFor="product_price"
                      className="text-sm font-medium flex items-center gap-2 text-foreground"
                    >
                      Harga Produk (Rp) *
                    </Label>
                    <Input
                      id="product_price"
                      name="product_price"
                      value={formatRupiah(form.product_price)}
                      onChange={handlePriceChange}
                      placeholder="50000"
                      type="text"
                      inputMode="numeric"
                      min={4}
                      max={999999999}
                      className="h-11 transition-smooth focus:ring-2 focus:ring-primary/20"
                      required
                    />
                    <p className="text-xs text-muted-foreground">
                      Minimal harga Rp 1.000.
                    </p>
                  </div>

                  {/* Sistem Produk */}
                  <div className="space-y-2">
                    <Label
                      htmlFor="product_system"
                      className="text-sm font-medium text-foreground"
                    >
                      Sistem Produk *
                    </Label>
                    <Select
                      value={form.product_system}
                      onValueChange={handleSystemChange}
                    >
                      <SelectTrigger className="h-11 transition-smooth focus:ring-2 focus:ring-primary/20">
                        <SelectValue placeholder="Pilih sistem produk" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ready">Ready</SelectItem>
                        <SelectItem value="preOrder">Pre-Order</SelectItem>
                      </SelectContent>
                      <p className="text-xs text-muted-foreground">
                      Produkmu Ready / Pre-Order?
                    </p>
                    </Select>
                  </div>

                  {/* Stock Produk - hanya muncul jika sistem produk adalah Ready */}
                  {form.product_system === "ready" && (
                    <div className="space-y-2">
                      <Label
                        htmlFor="product_stock"
                        className="text-sm font-medium text-foreground"
                      >
                        Jumlah Stok *
                      </Label>
                      <Input
                        id="product_stock"
                        name="product_stock"
                        value={form.product_stock}
                        onChange={handleChange}
                        placeholder="10"
                        type="number"
                        min="1"
                        className="h-11 transition-smooth focus:ring-2 focus:ring-primary/20"
                        required
                      />
                      <p className="text-xs text-muted-foreground">
                        Jumlah stok produk yang tersedia
                      </p>
                    </div>
                  )}

                  {/* Product Photo Upload */}
                  <div className="space-y-2">
                    <Label
                      htmlFor="product_photo"
                      className="text-sm font-medium flex items-center gap-2 text-foreground"
                    >
                      <ImageIcon className="h-4 w-4" />
                      Foto Produk
                    </Label>
                    <div className="relative">
                      <Input
                        id="product_photo"
                        ref={fileInputRef}
                        type="file"
                        accept="image/jpeg,image/jpg,image/png,image/webp"
                        onChange={handleFileChange}
                        className="h-11 file:mr-4 file:py-2 file:px-4 file:border-0 file:text-sm file:font-medium file:bg-primary file:text-primary-foreground hover:file:bg-primary/90 transition-smooth"
                      />
                    </div>
                    {form.product_photo && (
                      <p className="text-xs text-accent font-medium">
                        File dipilih: {form.product_photo.name}
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground">
                      Format: JPEG, PNG, WEBP (Maksimal 5MB)
                    </p>
                  </div>

                  <div className="space-y-2 md:col-span-2">
                    <Label
                      htmlFor="product_description"
                      className="text-sm font-medium text-foreground"
                    >
                      Deskripsi Produk
                    </Label>
                    <Textarea
                      id="product_description"
                      name="product_description"
                      value={form.product_description}
                      onChange={handleChange}
                      placeholder="Jelaskan detail produk, kondisi, spesifikasi, dll..."
                      className="min-h-[100px] transition-smooth focus:ring-2 focus:ring-primary/20"
                      maxLength={500}
                    />
                    <p className="text-xs text-muted-foreground">
                      Deskripsi detail membantu calon pembeli (opsional)
                    </p>
                  </div>
                </div>
              </div>

              <Separator className="bg-border" />

              {/* Payment Information Section */}
              <div className="space-y-6">
                <div className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5 text-primary" />
                  <h3 className="text-lg font-display font-semibold text-foreground">
                    Informasi Pembayaran
                  </h3>
                  <Badge
                    variant="outline"
                    className="text-primary border-primary/20 bg-primary/5"
                  >
                    Required
                  </Badge>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="space-y-2">
                    <Label
                      htmlFor="provider_bank"
                      className="text-sm font-medium text-foreground"
                    >
                      Bank Penerima *
                    </Label>
                    <Select
                      value={form.provider_bank}
                      onValueChange={handleBankChange}
                    >
                      <SelectTrigger className="h-11 transition-smooth focus:ring-2 focus:ring-primary/20">
                        <SelectValue placeholder="Pilih bank" />
                      </SelectTrigger>
                      <SelectContent>
                        {listProviderBank.map((bank) => (
                          <SelectItem key={bank} value={bank}>
                            {bank}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Nomor Rekening */}
                  <div className="space-y-2">
                    <Label
                      htmlFor="bank_account"
                      className="text-sm font-medium text-foreground"
                    >
                      Nomor Rekening *
                    </Label>
                    <Input
                      id="bank_account"
                      name="bank_account"
                      value={form.bank_account}
                      onChange={handleChange}
                      placeholder="1234567890"
                      type="number"
                      pattern="[0-9]*"
                      className="h-11 transition-smooth focus:ring-2 focus:ring-primary/20"
                      required
                      maxLength={20}
                    />
                    <p className="text-xs text-muted-foreground">
                      Nomor rekening untuk menerima pembayaran hasil penjualan
                    </p>
                  </div>

                  {/* Pemilik Nomor Rekening */}
                  <div className="space-y-2">
                    <Label
                      htmlFor="owner_bank_account"
                      className="text-sm font-medium text-foreground"
                    >
                      Nama Pemilik Rekening
                    </Label>
                    <Input
                      id="owner_bank_account"
                      name="owner_bank_account"
                      value={form.owner_bank_account}
                      onChange={handleChange}
                      placeholder="Bryan Waluyo Sasmitos"
                      className="h-11 transition-smooth focus:ring-2 focus:ring-primary/20"
                      maxLength={100}
                    />
                    <p className="text-xs text-muted-foreground">
                      A/n Pemilik Rekening
                    </p>
                  </div>
                </div>
              </div>
              <Separator className="bg-border" />

              {/* Submit Button */}
              <div className="pt-6">
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 text-lg font-display font-semibold bg-gradient-independence hover:shadow-independence transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Mengirim Permintaan...
                    </div>
                  ) : (
                    "Kirim Permintaan Titip Jual"
                  )}
                </Button>
                <p className="text-xs text-muted-foreground text-center mt-3">
                  Dengan mengirim formulir ini, Anda menyetujui syarat dan
                  ketentuan Bazaria
                </p>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SellRequestForm;
